import React, { useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import conversationMessagesQuery from '../../graphql/query/conversationMessages.query';
import markConversationReadMutation from '../../graphql/mutations/markConversationRead.mutation';
import Avatar from '@mui/material/Avatar';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MessageInput from './MessageInput';
import { resolveFirstListingImage, LISTING_IMAGE_FALLBACK, currencyFormat, resolveListingTitle } from '../../utils';
import './PrivateMessages.css';

interface User {
  id: number;
  firstname?: string;
  lastname?: string;
  email?: string;
  profilePictureUrl?: string;
}

interface Listing {
  id: number;
  make?: string;
  model?: string;
  year?: number;
  trim?: string;
  identityDisplayTitle?: string | null;
  price?: string | number;
  imageLoc?: any;
}

interface Message {
  id: number;
  conversationId: number;
  senderId: number;
  content: string;
  readAt: string | null;
  createdAt: string;
  userBySenderId?: User | null;
}

interface Conversation {
  id: number;
  listingId: number;
  buyerId: number;
  sellerId: number;
  createdAt: string;
  updatedAt: string;
  carsForSaleByListingId?: Listing | null;
  userByBuyerId?: User | null;
  userBySellerId?: User | null;
}

interface ConversationViewProps {
  conversation: Conversation;
  currentUserId: number;
  onBack?: () => void;
  darkMode?: boolean;
  onMessageSent?: () => void;
}

const FALLBACK_USER: User = { id: 0, firstname: 'User' };

const ConversationView: React.FC<ConversationViewProps> = ({
  conversation,
  currentUserId,
  onBack,
  darkMode = false,
  onMessageSent,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastMarkedUnreadKey = useRef<string>('');

  const { data, loading, refetch, error } = useQuery(conversationMessagesQuery, {
    variables: { conversationId: conversation.id },
    fetchPolicy: 'cache-and-network',
    pollInterval: 4000,
  });

  const [markConversationRead] = useMutation(markConversationReadMutation);

  const messages: Message[] = useMemo(
    () => (data?.conversationMessages?.nodes || []).filter(Boolean),
    [data?.conversationMessages?.nodes]
  );

  const otherUser =
    (currentUserId === conversation.buyerId
      ? conversation.userBySellerId
      : conversation.userByBuyerId) || FALLBACK_USER;

  const listing = conversation.carsForSaleByListingId || null;
  const listingTitle = listing ? resolveListingTitle(listing) : 'Listing';

  useEffect(() => {
    const unreadIds = messages
      .filter((m) => m.senderId !== currentUserId && !m.readAt)
      .map((m) => m.id)
      .sort((a, b) => a - b);
    if (unreadIds.length === 0) return;

    const key = `${conversation.id}:${unreadIds.join(',')}`;
    if (lastMarkedUnreadKey.current === key) return;
    lastMarkedUnreadKey.current = key;

    markConversationRead({
      variables: { conversationId: conversation.id },
    })
      .then(() => refetch())
      .catch(() => {
        if (lastMarkedUnreadKey.current === key) {
          lastMarkedUnreadKey.current = '';
        }
      });
  }, [messages, currentUserId, conversation.id, markConversationRead, refetch]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const getUserDisplayName = (user: User) => {
    if (user.firstname && user.lastname) {
      return `${user.firstname} ${user.lastname}`;
    }
    if (user.firstname) return user.firstname;
    return user.email?.split('@')[0] || 'User';
  };

  const getInitials = (user: User) => {
    if (user.firstname && user.lastname) {
      return `${user.firstname[0]}${user.lastname[0]}`.toUpperCase();
    }
    if (user.firstname) return user.firstname[0].toUpperCase();
    return user.email?.[0]?.toUpperCase() || 'U';
  };

  const getFirstImageUrl = (imageLoc: any): string =>
    resolveFirstListingImage(imageLoc, { darkMode });

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString();
  };

  const groupedMessages = messages.reduce((groups, message) => {
    const date = formatDate(message.createdAt);
    if (!groups[date]) groups[date] = [];
    groups[date].push(message);
    return groups;
  }, {} as Record<string, Message[]>);

  const handleMessageSent = () => {
    lastMarkedUnreadKey.current = '';
    refetch();
    onMessageSent?.();
  };

  const priceLabel =
    listing?.price != null && listing.price !== ''
      ? currencyFormat(parseFloat(String(listing.price)))
      : null;

  return (
    <div className={`conversation-view ${darkMode ? 'dark' : ''}`} data-testid="conversation-view">
      <div className="conversation-view-header">
        {onBack && (
          <button type="button" className="back-button" onClick={onBack} aria-label="Back to inbox">
            <ArrowBackIcon />
          </button>
        )}
        <Avatar
          src={otherUser.profilePictureUrl || undefined}
          sx={{ width: 40, height: 40, fontSize: 14 }}
        >
          {getInitials(otherUser)}
        </Avatar>
        <div className="header-info">
          <span className="header-user-name">{getUserDisplayName(otherUser)}</span>
          <span className="header-listing-title">{listingTitle}</span>
          {priceLabel && <span className="header-listing-price">{priceLabel}</span>}
        </div>
        {listing?.id ? (
          <Link
            to={`/product/${listing.id}`}
            className="header-listing-link"
            title="View listing"
            aria-label="View listing"
          >
            <div className="header-listing-image">
              <img
                src={getFirstImageUrl(listing.imageLoc)}
                alt={listingTitle}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = LISTING_IMAGE_FALLBACK;
                }}
              />
              <span className="header-listing-open">
                <OpenInNewIcon fontSize="inherit" />
              </span>
            </div>
          </Link>
        ) : (
          <div className="header-listing-image">
            <img src={LISTING_IMAGE_FALLBACK} alt="" />
          </div>
        )}
      </div>

      <div className="messages-container">
        {loading && messages.length === 0 && (
          <div className="messages-loading">Loading messages…</div>
        )}

        {error && messages.length === 0 && (
          <div className="messages-error" role="alert">
            Could not load messages. {error.message}
          </div>
        )}

        {Object.entries(groupedMessages).map(([date, dateMessages]) => (
          <div key={date} className="message-group">
            <div className="message-date-divider">
              <span>{date}</span>
            </div>
            {dateMessages.map((message) => {
              const isOwn = message.senderId === currentUserId;
              const sender = message.userBySenderId || (isOwn ? null : otherUser);
              return (
                <div
                  key={message.id}
                  className={`message-item ${isOwn ? 'own' : 'other'}`}
                >
                  {!isOwn && (
                    <Avatar
                      src={sender?.profilePictureUrl || undefined}
                      sx={{ width: 32, height: 32, fontSize: 12 }}
                    >
                      {getInitials(sender || FALLBACK_USER)}
                    </Avatar>
                  )}
                  <div className="message-bubble">
                    <p className="message-content">{message.content}</p>
                    <span className="message-time">{formatTime(message.createdAt)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        ))}

        {messages.length === 0 && !loading && !error && (
          <div className="no-messages">
            <p>Start the conversation</p>
            <span>
              Ask about availability, price, history, or schedule a viewing for the{' '}
              {listingTitle || 'vehicle'}.
            </span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <MessageInput
        conversationId={conversation.id}
        onMessageSent={handleMessageSent}
        darkMode={darkMode}
        showQuickReplies={messages.length === 0 && !loading}
      />
    </div>
  );
};

export default ConversationView;
